import {
  historyBoundsMinYear,
  SLIDER_MAX_YEAR,
  timeFromYear,
  timeToYear,
  timeFrom,
  timeTo,
  setTimeWindow,
} from "../state/store";

/** The time control: a dual-handle year range over the ONE time axis — archive start
 *  (1842 today) through next season. Dragging writes year-aligned bounds to the same
 *  [timeFrom, timeTo] the precise date inputs edit; the right handle at the max means
 *  "no end bound" so upcoming concerts never get clipped by a year constant.
 *
 *  Two native `<input type="range">` stacked on one track — each is `pointer-events: none`
 *  in CSS except its own thumb (`::-webkit-slider-thumb` / `::-moz-range-thumb` get
 *  `pointer-events: auto`), the standard accessible way to build a dual-range control
 *  without a pointer-drag reimplementation: both thumbs stay keyboard-focusable and
 *  independently draggable, and nothing here needs its own mouse/touch handlers. */
export function HistorySlider() {
  const min = historyBoundsMinYear.value;
  const max = SLIDER_MAX_YEAR;
  const from = Math.max(min, Math.min(timeFromYear.value, max));
  const to = Math.max(from, Math.min(timeToYear.value, max));
  const span = Math.max(1, max - min);

  function applyYears(fromYear: number, toYear: number) {
    const lo = Math.min(fromYear, toYear);
    const hi = Math.max(fromYear, toYear);
    setTimeWindow(`${lo}-01-01`, hi >= max ? "" : `${hi}-12-31`);
  }

  function handleFromInput(e: Event) {
    const v = Math.min(Number((e.currentTarget as HTMLInputElement).value), to);
    applyYears(v, to);
  }
  function handleToInput(e: Event) {
    const v = Math.max(Number((e.currentTarget as HTMLInputElement).value), from);
    applyYears(from, v);
  }

  const pctFrom = ((from - min) / span) * 100;
  const pctTo = ((to - min) / span) * 100;

  // Show exact dates when the window is finer than whole years (set via the date
  // inputs); otherwise the year span reads cleaner.
  const preciseFrom = !timeFrom.value.endsWith("-01-01");
  const preciseTo = Boolean(timeTo.value) && !timeTo.value.endsWith("-12-31");
  const label =
    preciseFrom || preciseTo
      ? `${timeFrom.value} – ${timeTo.value || "now"}`
      : from === to
        ? String(from)
        : `${from} – ${to}`;

  // One compact row — [window label | track] — the same height as the text inputs beside
  // it, so the filter row reads as one aligned line instead of a taller outlier. Clearing
  // a bound lives on the From/To date inputs' "×", not here.
  return (
    <div class="history-slider" role="group" aria-label="Time period">
      <span class="history-slider-window">{label}</span>
      <div class="history-slider-track-wrap">
        <div class="history-slider-track">
          {/* The track is inset 8px from each edge to match where a native range thumb can
              actually reach (browsers stop it half a thumb-width short of 0%/100%), so the
              fill has to follow the same inset math rather than a plain percentage of the
              full wrap width. */}
          <div
            class="history-slider-fill"
            style={{
              left: `calc(8px + (100% - 16px) * ${(pctFrom / 100).toFixed(4)})`,
              width: `calc((100% - 16px) * ${((pctTo - pctFrom) / 100).toFixed(4)})`,
            }}
          />
        </div>
        <input
          type="range"
          class="history-slider-input"
          min={min}
          max={max}
          value={from}
          onInput={handleFromInput}
          aria-label={`Start year: ${from}`}
        />
        <input
          type="range"
          class="history-slider-input"
          min={min}
          max={max}
          value={to}
          onInput={handleToInput}
          aria-label={`End year: ${to}`}
        />
      </div>
    </div>
  );
}
